import React, { useState } from 'react';
import { Alert, Box, Tab, Typography } from '@mui/material';
import { DEEP_GREEN, MUTED } from '../colorTokens';
import ZoomSafeTabs from '../../common/ZoomSafeTabs';
import { hasOperationalExcellenceAccess } from '../../../services/authService';
import LoggingInventoryPage from '../logging-inventory/LoggingInventoryPage';
import DatadogMonitorsPage from '../datadog-monitoring/DatadogMonitorsPage';
import DatadogLogAnalyzerPanel from '../DatadogLogAnalyzerPanel';

// Matches the OperationalExcellenceHub banner (dark green gradient, bold
// title + "Operational Excellence" subtitle) -- kept local since GREEN_LIGHT
// isn't exported from colorTokens.
const GREEN_LIGHT = '#2C4A3A';

// Logging Inventory tab matches HighQ Vital Signs and is reused as-is (not
// duplicated) so both pages stay in sync automatically. DataDog Monitors is
// specific to this console.
const AUDIT_CONSOLE_TABS = [
  { id: 'datadoganalyzer', label: 'Datadog Log Analyzer' },
  { id: 'datadogmonitors', label: 'DataDog Monitors' },
  { id: 'logginginventory', label: 'Logging Inventory' }
];

const ANALYZER_VIEWS = [
  { id: 'issues', label: 'Issues Dashboard' },
  { id: 'coverage', label: 'Monitor Gap Coverage' },
  { id: 'alerts', label: 'Alert Coverage' }
];

const AuditConsolePage = () => {
  const [activeTab, setActiveTab] = useState(AUDIT_CONSOLE_TABS[0].id);
  const [analyzerView, setAnalyzerView] = useState(ANALYZER_VIEWS[0].id);
  const [analyzerVisited, setAnalyzerVisited] = useState(AUDIT_CONSOLE_TABS[0].id === 'datadoganalyzer');

  // Self-guards against direct URL access (/audit-console), same as
  // OperationalExcellenceAccessGate does for /operational-excellence --
  // the sidebar link is hidden without access, but the route itself must
  // not rely on that alone.
  if (!hasOperationalExcellenceAccess()) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ maxWidth: 520 }}>
          You do not have access to Operational Excellence. Ask an administrator to grant it in Admin → User Management.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <Box
        sx={{
          borderRadius: '12px',
          background: `linear-gradient(120deg, ${DEEP_GREEN}, ${GREEN_LIGHT})`,
          color: '#fff',
          px: { xs: 2, md: 2.5 },
          py: { xs: 1.5, md: 2 }
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
          HighQ Audit Console
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', mt: 0.35, fontWeight: 500 }}>
          Operational Excellence
        </Typography>
      </Box>

      <ZoomSafeTabs
        value={activeTab}
        onChange={(_e, value) => {
          setActiveTab(value);
          if (value === 'datadoganalyzer') setAnalyzerVisited(true);
        }}
        sx={{
          minHeight: 0,
          borderBottom: '1px solid rgba(26,46,34,0.12)',
          mt: 2,
          mb: 2.5,
        }}
        indicatorColor={DEEP_GREEN}
      >
        {AUDIT_CONSOLE_TABS.map((tab) => (
          <Tab
            key={tab.id}
            value={tab.id}
            label={tab.label}
            id={`audit-console-tab-${tab.id}`}
            aria-controls={`audit-console-tabpanel-${tab.id}`}
            sx={{
              minHeight: 0,
              textTransform: 'none',
              fontFamily: 'inherit',
              fontWeight: 600,
              fontSize: '0.9375rem',
              letterSpacing: '0.01em',
              px: { xs: 1.5, sm: 2.5 },
              py: 1.25,
              color: MUTED,
              '&:hover': { color: DEEP_GREEN },
              '&.Mui-selected': { color: DEEP_GREEN }
            }}
          />
        ))}
      </ZoomSafeTabs>

      <Box role="tabpanel" id="audit-console-tabpanel-logginginventory" aria-labelledby="audit-console-tab-logginginventory" hidden={activeTab !== 'logginginventory'}>
        {activeTab === 'logginginventory' && <LoggingInventoryPage />}
      </Box>
      {/* Stays mounted once opened so the analyzer keeps its filters when switching tabs. */}
      <Box role="tabpanel" id="audit-console-tabpanel-datadoganalyzer" aria-labelledby="audit-console-tab-datadoganalyzer" hidden={activeTab !== 'datadoganalyzer'}>
        {analyzerVisited && (
          <>
            <ZoomSafeTabs
              value={analyzerView}
              onChange={(_e, value) => setAnalyzerView(value)}
              aria-label="Datadog Log Analyzer views"
              sx={{
                minHeight: 0,
                mb: 2,
              }}
              indicatorColor={DEEP_GREEN}
              indicatorHeight={2}
            >
              {ANALYZER_VIEWS.map((view) => (
                <Tab
                  key={view.id}
                  value={view.id}
                  label={view.label}
                  sx={{
                    minHeight: 0,
                    textTransform: 'none',
                    fontFamily: 'inherit',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    py: 1,
                    color: MUTED,
                    '&.Mui-selected': { color: DEEP_GREEN }
                  }}
                />
              ))}
            </ZoomSafeTabs>
            <DatadogLogAnalyzerPanel activeTab={analyzerView} />
          </>
        )}
      </Box>
      <Box role="tabpanel" id="audit-console-tabpanel-datadogmonitors" aria-labelledby="audit-console-tab-datadogmonitors" hidden={activeTab !== 'datadogmonitors'}>
        {activeTab === 'datadogmonitors' && <DatadogMonitorsPage />}
      </Box>
    </Box>
  );
};

export default AuditConsolePage;
